import { useState } from 'react';

interface FormData {
  [key: string]: string;
}

interface FormSubmitState {
  isSubmitting: boolean;
  isSuccess: boolean;
  isError: boolean;
  message: string;
}

// Get the API URL from environment variables, with a fallback
const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || 'https://forms.financesunshine.com';
};

const useFormSubmit = (endpoint?: string) => {
  // Use provided endpoint or get from environment variables
  const apiEndpoint = endpoint || getApiUrl();
  const [state, setState] = useState<FormSubmitState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
    message: '',
  });

  const submitForm = async (data: FormData) => {
    // Reset state
    setState({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
      message: '',
    });

    try {
      // Create FormData object
      const formData = new FormData();
      
      // Add all form fields to FormData
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // Send the request
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        body: formData,
      });

      // Parse the response
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Something went wrong');
      }

      // Handle success
      setState({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
        message: result.message || 'Form submitted successfully!',
      });

      return { success: true, message: result.message };
    } catch (error) {
      // Handle error
      console.error('Form submission error:', error);
      
      setState({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
      });

      return { success: false, message: state.message };
    }
  };

  const resetForm = () => {
    setState({
      isSubmitting: false,
      isSuccess: false,
      isError: false,
      message: '',
    });
  };

  return {
    submitForm,
    resetForm,
    isSubmitting: state.isSubmitting,
    isSuccess: state.isSuccess,
    isError: state.isError,
    message: state.message,
  };
};

export default useFormSubmit;
